/**
 * Locale + currency aware money formatting (ADR 079).
 *
 * A price is not "symbol + amount": placement, separators and the number of
 * decimals are all decided by the locale/currency pair. English/EUR is `€10.00`,
 * French/EUR is `10,00 €`, Swiss francs read `CHF 10.00`, and yen has no decimal
 * part at all. Intl.NumberFormat knows all of that; string concatenation does not.
 */

/** Intl formatters are expensive to construct and immutable — build each once. */
const cache = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, currency: string): Intl.NumberFormat {
  const key = `${locale}:${currency}`;
  const hit = cache.get(key);
  if (hit) return hit;

  let made: Intl.NumberFormat;
  try {
    made = new Intl.NumberFormat(locale, { style: 'currency', currency });
  } catch {
    // Unknown locale or currency code (an older place, a hand-edited row): fall
    // back to a valid formatter rather than throwing inside a render.
    made = new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR' });
  }
  cache.set(key, made);
  return made;
}

/**
 * Format an amount in a place's currency for a viewer's language.
 *
 * @param amount   the numeric amount (10 → "€10.00")
 * @param currency ISO 4217 code from the place (e.g. "EUR", "GBP", "JPY")
 * @param locale   the viewer's language (one of our 8 locales, or any BCP 47 tag)
 */
export function formatMoney(amount: number, currency: string, locale: string): string {
  if (!Number.isFinite(amount)) return '';
  return formatter(locale || 'en', (currency || 'EUR').toUpperCase()).format(amount);
}

/**
 * The symbol a currency renders with in a given language, e.g. GBP → "£", PLN → "zł".
 *
 * Whether a currency HAS a symbol is a property of the locale, not the currency:
 * CLDR gives no short form for AED/SAR/MAD in English (the code is what English
 * writes), while Arabic renders "د.إ." / "ر.س." / "د.م.". Swiss francs have no
 * glyph in any locale. Callers get the code back in those cases and should not
 * print it twice — see the currency picker.
 */
export function currencySymbol(currency: string, locale: string): string {
  const code = (currency || 'EUR').toUpperCase();
  try {
    const part = new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: code,
      // Narrow gives the real glyph where one exists ("kr", "₺", "$"); the picker
      // shows the ISO code alongside, so the shortest unambiguous form is right.
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? code;
  } catch {
    return code;
  }
}

/**
 * Same, from the string prices the API returns. Returns '' for a missing price and
 * echoes back anything unparseable, so a bad row shows its raw value instead of NaN.
 */
export function formatPriceString(
  price: string | undefined,
  currency: string,
  locale: string,
): string {
  if (price === undefined || price === null || price === '') return '';
  const n = parseFloat(price);
  return Number.isNaN(n) ? price : formatMoney(n, currency, locale);
}
